'use client';

import React from 'react';
import { FLOCK_OPTIONS, THRESHOLD_CONFIGS } from './mortalityData';

export default function FlockMortalityStats() {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-6">
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-800">Flock Mortality Statistics</h3>
        <p className="text-xs text-gray-400 mt-0.5">Cumulative mortality, survival rate, and threshold status per active flock</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead>
            <tr className="border-b border-gray-50 bg-gray-50">
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Flock</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">House</th>
              <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500">Initial</th>
              <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500">Current</th>
              <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500">Total Deaths</th>
              <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500">Mortality %</th>
              <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500">Survival Rate</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Threshold Status</th>
            </tr>
          </thead>
          <tbody>
            {FLOCK_OPTIONS?.map(flock => {
              const threshold = THRESHOLD_CONFIGS?.find(t => t?.flockId === flock?.id);
              const mortalityPct = ((flock?.totalMortality / flock?.initialQty) * 100);
              const survivalRate = 100 - mortalityPct;
              const atRisk = threshold ? mortalityPct >= threshold?.cumulativeThresholdPct * 0.8 : false;
              const breached = threshold ? mortalityPct >= threshold?.cumulativeThresholdPct : false;

              return (
                <tr key={flock?.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="text-xs font-semibold text-gray-800">{flock?.flockNumber}</p>
                    <p className="text-xs text-gray-400 truncate max-w-[140px]">{flock?.flockName}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{flock?.house}</td>
                  <td className="px-4 py-3 text-xs text-right text-gray-700">{flock?.initialQty?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs text-right font-semibold text-gray-800">{flock?.currentQty?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs text-right text-red-600 font-semibold">{flock?.totalMortality?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={`text-xs font-bold ${breached ? 'text-red-600' : atRisk ? 'text-amber-600' : 'text-gray-700'}`}>
                      {mortalityPct?.toFixed(2)}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {/* Survival bar */}
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${survivalRate >= 97 ? 'bg-green-500' : survivalRate >= 94 ? 'bg-amber-400' : 'bg-red-500'}`}
                          style={{ width: `${Math.min(100, survivalRate)}%` }}
                        />
                      </div>
                      <span className={`text-xs font-semibold ${survivalRate >= 97 ? 'text-green-600' : survivalRate >= 94 ? 'text-amber-600' : 'text-red-600'}`}>
                        {survivalRate?.toFixed(2)}%
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {breached ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                        ⚠ Threshold Breached
                      </span>
                    ) : atRisk ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                        ⚡ Approaching Limit
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                        ✓ Within Threshold
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
